import { useEffect, useState } from 'react'
import { GroupService } from '../../services/groups'
import type { GroupWithMemberCount } from '../../services/groups'
import { formatNumber } from '../../utils/formatNumber'

interface GroupSelectorProps {
  schoolId: string
  selectedGroupId: string | null
  onGroupChange: (groupId: string | null) => void
  disabled?: boolean
}

const GroupSelector = ({
  schoolId,
  selectedGroupId,
  onGroupChange,
  disabled = false
}: GroupSelectorProps) => {
  const [groups, setGroups] = useState<GroupWithMemberCount[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const loadGroups = async () => {
      if (!schoolId) return

      try {
        setIsLoading(true)
        setError('')
        const availableGroups = await GroupService.getSchoolGroups(schoolId)
        setGroups(availableGroups)
      } catch (err) {
        console.error('Failed to load groups:', err)
        setError('グループの読み込みに失敗しました')
      } finally {
        setIsLoading(false)
      }
    }

    loadGroups()
  }, [schoolId])

  const handleChange = (value: string) => {
    const newGroupId = value === 'all' ? null : value
    onGroupChange(newGroupId)
  }

  return (
    <div className="flex items-center gap-2">
      <label className="text-sm font-medium text-gray-700 whitespace-nowrap">グループ</label>
      <select
        value={selectedGroupId || 'all'}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled || isLoading}
        className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white text-sm ${
          disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <option value="all">全て</option>
        {groups.map(group => (
          <option key={group.id} value={group.id}>
            {group.name} ({formatNumber(group.member_count)}名)
          </option>
        ))}
      </select>
      {error && (
        <span className="text-xs text-red-600">{error}</span>
      )}
    </div>
  )
}

export default GroupSelector
